import type { AsesoriaPlan } from '../../../../hooks/useSupabaseAdmin';

const PAGE_SIZE = 10;

interface PlanTableProps {
  plans: AsesoriaPlan[];
  draggingId: number | null;
  dragOverId: number | null;
  onDragStart: (e: React.DragEvent<HTMLTableRowElement>, id: number) => void;
  onDragOver: (e: React.DragEvent<HTMLTableRowElement>, id: number) => void;
  onDrop: (e: React.DragEvent<HTMLTableRowElement>, toId: number) => void;
  onDragEnd: () => void;
  onEdit: (plan: AsesoriaPlan) => void;
  onDelete: (plan: AsesoriaPlan) => void;
  page: number;
  onPageChange: (p: number) => void;
}

export default function PlanTable({
  plans,
  draggingId,
  dragOverId,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onEdit,
  onDelete,
  page,
  onPageChange,
}: PlanTableProps) {
  const totalPages = Math.max(1, Math.ceil(plans.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const paged = plans.slice(startIdx, startIdx + PAGE_SIZE);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-gray-50">
              <tr>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3 w-10">
                  Orden
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Plan
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Precio
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Destacado
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Características
                </th>
                <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-gray-200">
              {paged.map((plan) => (
                <tr
                  key={plan.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, plan.id)}
                  onDragOver={(e) => onDragOver(e, plan.id)}
                  onDrop={(e) => onDrop(e, plan.id)}
                  onDragEnd={onDragEnd}
                  className={`hover:bg-brand-gray-50/50 transition-colors cursor-move ${
                    draggingId === plan.id ? 'opacity-40' : ''
                  } ${dragOverId === plan.id ? 'bg-brand-sage/20' : ''}`}
                >
                  <td className="px-5 py-4">
                    <div className="w-6 h-6 flex items-center justify-center text-brand-gray-400">
                      <i className="ri-draggable text-lg" />
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-sans text-brand-black text-sm font-medium">
                      {plan.name}
                    </p>
                    <p className="font-sans text-brand-gray-400 text-xs mt-0.5 line-clamp-1 max-w-[260px]">
                      {plan.description}
                    </p>
                  </td>
                  <td className="px-5 py-4 font-sans text-brand-gray-600 text-sm">
                    {plan.price}
                    <span className="block text-xs text-brand-gray-400 mt-0.5">
                      {plan.period}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    {plan.highlighted ? (
                      <span className="inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-brand-sage text-brand-black">
                        Destacado
                      </span>
                    ) : (
                      <span className="inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-brand-gray-100 text-brand-gray-500">
                        Normal
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 font-sans text-brand-gray-600 text-sm">
                    {plan.features.length}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onEdit(plan)}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                        title="Editar"
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        onClick={() => onDelete(plan)}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {plans.length === 0 && (
          <div className="p-10 text-center">
            <p className="font-sans text-brand-gray-400 text-sm">
              No se encontraron planes
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white border border-brand-gray-200 rounded-2xl px-5 py-3">
          <div className="font-sans text-brand-gray-500 text-sm">
            Mostrando {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, plans.length)} de{" "}
            {plans.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(Math.max(1, safePage - 1))}
              disabled={safePage <= 1}
              className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={`w-8 h-8 rounded-lg font-sans text-sm flex items-center justify-center transition-colors ${
                  p === safePage
                    ? 'bg-brand-black text-white'
                    : 'border border-brand-gray-200 text-brand-gray-600 hover:border-brand-black hover:text-brand-black'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
              disabled={safePage >= totalPages}
              className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}